<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { resolveNavigationTarget } from "#bb/lib/navigationTarget";
import { resolveSiteFileDownloadUrl } from "#bb/lib/pathContract";

const logger = useLogger("Navigation-Header");

// 1. Expect 'id' from the parent.
const props = defineProps<{ id: string }>();

// 2. Access the definition data (read-only) from the content manager.
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));
const features = inject<{
  resourceDefinitionPath?: string | null;
}>("render-features", {});
const { resolveHref } = useAppRoute();
const { siteId } = useSite();
const config = useRuntimeConfig();
const logoSource = computed(() => resolveSiteFileDownloadUrl(
  definitionData.value?.logo,
  config.public.apiPublicUrl || config.public.apiUrl,
  siteId.value,
  features.resourceDefinitionPath,
));
const menuItems = computed(() =>
  (definitionData.value?.menuItems ?? []).map((item: any) => {
    const target = resolveNavigationTarget(item.url, {
      resourceDefinitionPath: features.resourceDefinitionPath,
      resolvePath: resolveHref,
    });
    return {
      ...item,
      url: target?.href || "#",
      external: item.external ?? target?.external ?? false,
    };
  }),
);


// 4. Define a context menu "Edit" command.
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Navigation editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this navigation header",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Navigation Header",
    icon: "lucide:panel-top",
  },
});
</script>

<template>
  <nav>
    <div class="flex h-16 items-center">
      <nuxt-link to="/">
        <span class="sr-only">{{ definitionData.name }}</span>
        <img class="h-16 w-auto" :src="logoSource" alt="logo" />
      </nuxt-link>
      <div class="mx-6">
        <Button
          variant="ghost"
          v-for="menuItem in menuItems"
          :key="menuItem.id"
          as-child
        >
          <nuxt-link
            v-if="!menuItem.disabled"
            :to="menuItem.url"
            :external="menuItem.external"
            :target="menuItem.target"
            active-class="text-primary"
            class="text-sm font-medium transition-colors hover:text-primary"
          >
            {{ menuItem.label }}
          </nuxt-link>
          <span v-else class="text-sm font-medium text-muted-foreground">{{
            menuItem.label
          }}</span>
        </Button>
      </div>
      <div class="ml-auto flex items-center space-x-4"></div>
    </div>
  </nav>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.NAVIGATION_HEADER,
  icon: "lucide:panel-top",
  type: "Navigation Header",
  description: "A navigation header for your application.",
  editor: () => import("#bb/components/editor/navigation/index.vue"),
  default: {
    name: "Navigation Header",
    logo: "/assets/logo/charcoal-orange.svg",
    menuItems: [
      {
        id: "home",
        label: "Home",
        url: "/",
        external: false,
        target: "_self",
        order: 1,
        disabled: false,
      },
      {
        id: "about",
        label: "About",
        url: "/about",
        external: false,
        target: "_self",
        order: 2,
        disabled: false,
      },
      {
        id: "contact",
        label: "Contact",
        url: "/contact",
        external: false,
        target: "_self",
        order: 3,
        disabled: false,
      },
    ],
  },
  categories: ["Navigation"],
  order: 160101,
};
</script>
